import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { documentsService } from '../../../services/partnerService';

const LegalDocumentsSection = ({ partnerId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState(null);

  useEffect(() => {
    if (partnerId) loadDocuments();
  }, [partnerId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await documentsService?.getByPartner(partnerId);
      setDocuments(data || []);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const url = await documentsService?.getSignedUrl(doc?.file_path);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error getting download URL:', err);
    }
  };

  const getDocTypeIcon = (docType) => {
    const icons = {
      'contract': 'FileText',
      'nda': 'Scale',
      'certification': 'Award',
      'compliance': 'Shield',
      'insurance': 'ShieldCheck',
      'financial': 'DollarSign'
    };
    const key = docType?.toLowerCase();
    return icons?.[key] || 'File';
  };

  const getDocTypeLabel = (docType) => {
    return docType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'Document';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const activeCount = documents?.length;
  const certCount = documents?.filter(d => d?.doc_type?.toLowerCase()?.includes('cert'))?.length;

  if (loading) {
    return (
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
        <div className="flex items-center gap-12 mb-24">
          <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
            <Icon name="FileText" size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Legal Documents</h2>
            <p className="text-sm text-slate-600">Contracts, certifications, and compliance documents</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-48">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
      <div className="flex items-center justify-between mb-24">
        <div className="flex items-center gap-12">
          <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
            <Icon name="FileText" size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Legal Documents</h2>
            <p className="text-sm text-slate-600">Contracts, certifications, and compliance documents</p>
          </div>
        </div>
      </div>

      {/* Document Stats */}
      <div className="grid grid-cols-3 gap-16 mb-32">
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-20 border border-blue-100">
          <div className="flex items-center justify-between mb-8">
            <span className="text-sm font-medium text-slate-600">Total Documents</span>
            <Icon name="Files" size={18} className="text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{activeCount}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-20 border border-green-100">
          <div className="flex items-center justify-between mb-8">
            <span className="text-sm font-medium text-slate-600">Uploaded</span>
            <Icon name="CheckCircle" size={18} className="text-success" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{activeCount}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-20 border border-purple-100">
          <div className="flex items-center justify-between mb-8">
            <span className="text-sm font-medium text-slate-600">Certifications</span>
            <Icon name="Award" size={18} className="text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900">{certCount}</div>
        </div>
      </div>

      {/* Documents List */}
      {documents?.length > 0 ? (
        <div className="space-y-12">
          {documents?.map((doc) => {
            const isExpanded = expandedDoc === doc?.id;
            return (
              <div key={doc?.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-elevation-md transition-smooth">
                <div className="p-20">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-16 flex-1">
                      <div className="p-12 bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg">
                        <Icon name={getDocTypeIcon(doc?.doc_type)} size={24} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-12 mb-8">
                          <h4 className="font-semibold text-slate-900">{doc?.file_name}</h4>
                          <span className="px-10 py-4 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                            {getDocTypeLabel(doc?.doc_type)}
                          </span>
                        </div>
                        <div className="flex items-center gap-20 text-sm text-slate-500">
                          <div className="flex items-center gap-6">
                            <Icon name="Calendar" size={14} />
                            <span>Uploaded: {new Date(doc?.uploaded_at)?.toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-6">
                            <Icon name="HardDrive" size={14} />
                            <span>{formatFileSize(doc?.file_size)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-10 bg-slate-50 hover:bg-slate-100 rounded-lg transition-smooth"
                        title="Download"
                      >
                        <Icon name="Download" size={18} className="text-slate-600" />
                      </button>
                      <button
                        onClick={() => setExpandedDoc(isExpanded ? null : doc?.id)}
                        className="p-10 bg-slate-50 hover:bg-slate-100 rounded-lg transition-smooth"
                      >
                        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-slate-600" />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-20 pt-20 border-t border-slate-100">
                      <div className="grid grid-cols-2 gap-16">
                        <div>
                          <div className="text-xs font-medium text-slate-500 mb-8">Document Details</div>
                          <div className="space-y-8 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">File Name:</span>
                              <span className="font-medium text-slate-900 truncate ml-8">{doc?.file_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Type:</span>
                              <span className="font-medium text-slate-900">{getDocTypeLabel(doc?.doc_type)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Size:</span>
                              <span className="font-medium text-slate-900">{formatFileSize(doc?.file_size)}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-500 mb-8">Actions</div>
                          <div className="space-y-8">
                            <button
                              onClick={() => handleDownload(doc)}
                              className="w-full px-16 py-10 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-smooth flex items-center justify-center gap-8"
                            >
                              <Icon name="Download" size={16} />
                              Download Document
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-48">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-16">
            <Icon name="FileText" size={32} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-8">No documents uploaded yet</h3>
          <p className="text-sm text-slate-600">Documents uploaded during registration will appear here.</p>
        </div>
      )}
    </div>
  );
};

export default LegalDocumentsSection;